import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import {
  Radio,
  MapPin,
  ChevronRight,
  Globe,
  Search,
  Loader2,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FrequencyIndexPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedState, setExpandedState] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/frequencies/seo/index`);
        if (res.ok) {
          const json = await res.json();
          setData(json);
        }
      } catch (err) {
        console.error('Failed to fetch index:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  const pageTitle = "Police & Fire Scanner Frequencies by State | VeriWatch";
  const pageDesc = `Browse free scanner frequencies for police, fire, and EMS in all 50 US states. ${data?.total_cities || 0}+ cities covered with verified frequencies.`;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "US Scanner Frequencies Directory",
    "description": pageDesc,
    "url": "https://veriwatch.io/frequencies/browse",
    "provider": {
      "@type": "Organization",
      "name": "VeriWatch",
      "url": "https://veriwatch.io"
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content="https://veriwatch.io/frequencies/browse" />
        <link rel="canonical" href="https://veriwatch.io/frequencies/browse" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Header */}
        <div className="border-b border-slate-800 bg-slate-900/50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-slate-400 hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4 text-slate-600" />
              <Link to="/frequencies" className="text-slate-400 hover:text-white">Frequencies</Link>
              <ChevronRight className="w-4 h-4 text-slate-600" />
              <span className="text-teal-400">Browse All</span>
            </nav>
          </div>
        </div>
        
        {/* Hero */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-12">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-8 h-8 text-teal-400" />
              <h1 className="text-3xl md:text-4xl font-bold">
                Scanner Frequencies by State
              </h1>
            </div>
            <p className="text-xl text-slate-400 mb-6">
              Browse police, fire & EMS frequencies for every US state
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="bg-slate-800/50 rounded-lg px-4 py-2">
                <span className="text-2xl font-bold text-teal-400">{data?.total_states || 0}</span>
                <span className="text-slate-400 ml-2">States</span>
              </div>
              <div className="bg-slate-800/50 rounded-lg px-4 py-2">
                <span className="text-2xl font-bold text-blue-400">{data?.total_cities || 0}</span>
                <span className="text-slate-400 ml-2">Cities</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* State grid */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data?.states?.map((state) => (
              <div 
                key={state.code}
                className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden hover:border-teal-500/30 transition-colors"
              >
                <Link 
                  to={`/frequencies/${state.code.toLowerCase()}`}
                  className="block p-4 hover:bg-slate-800/70 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-teal-400" />
                      {state.name}
                    </h2>
                    <span className="text-sm text-teal-400 font-mono">{state.code}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span>{state.total_frequencies} frequencies</span>
                    <span>{state.cities.length} cities</span>
                  </div>
                </Link>
                
                {/* City links */}
                {state.cities.length > 0 && (
                  <div className="border-t border-slate-700 p-3 bg-slate-900/30">
                    <button
                      onClick={() => setExpandedState(expandedState === state.code ? null : state.code)}
                      className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-2"
                    >
                      <Building2 className="w-3 h-3" />
                      {expandedState === state.code ? 'Hide cities' : `Show ${state.cities.length} cities`}
                      <ChevronRight className={`w-3 h-3 transition-transform ${expandedState === state.code ? 'rotate-90' : ''}`} />
                    </button>
                    
                    {expandedState === state.code && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {state.cities.slice(0, 10).map(city => (
                          <Link
                            key={city.slug}
                            to={`/frequencies/${state.code.toLowerCase()}/${city.slug}`}
                            className="text-xs px-2 py-1 bg-slate-800 hover:bg-slate-700 rounded transition-colors"
                          >
                            {city.name} ({city.frequency_count})
                          </Link>
                        ))}
                        {state.cities.length > 10 && (
                          <Link
                            to={`/frequencies/${state.code.toLowerCase()}`}
                            className="text-xs px-2 py-1 bg-teal-900/30 text-teal-400 hover:bg-teal-900/50 rounded transition-colors"
                          >
                            +{state.cities.length - 10} more
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* National channels CTA */}
          <div className="mt-12 p-6 bg-emerald-900/20 border border-emerald-800 rounded-lg text-center">
            <Globe className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-emerald-300 mb-2">National Interoperability Channels</h3>
            <p className="text-emerald-200/70 mb-4 max-w-xl mx-auto">
              Don't forget the NIFOG channels - these federal standard frequencies work in every state for emergency communications.
            </p>
            <Link to="/frequencies">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                View National Channels
              </Button>
            </Link>
          </div>
          
          {/* SEO content */}
          <div className="mt-12 prose prose-invert max-w-none">
            <h2>Free Scanner Frequencies for All 50 States</h2>
            <p>
              VeriWatch provides the most comprehensive free database of police, fire, and EMS scanner 
              frequencies in the United States. Our data comes from official FCC records and is verified 
              by our community of scanner enthusiasts.
            </p>
            <h3>What You'll Find</h3>
            <ul>
              <li><strong>Police Frequencies</strong> - Local police departments, state police, and sheriff's offices</li>
              <li><strong>Fire Frequencies</strong> - Fire department dispatch and tactical channels</li>
              <li><strong>EMS Frequencies</strong> - Ambulance and medical services</li>
              <li><strong>Interoperability Channels</strong> - Multi-agency coordination frequencies</li>
            </ul>
            <h3>Why VeriWatch?</h3>
            <p>
              Unlike paid services, VeriWatch is completely free. Every frequency shows its source 
              (FCC or community submitted), verification status, and confidence score. We believe 
              in full transparency - you deserve to know where your data comes from.
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <footer className="border-t border-slate-800 bg-slate-900/50 mt-12">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Radio className="w-5 h-5 text-teal-400" />
                <span className="font-bold">VeriWatch</span>
                <span className="text-slate-400">- Free Scanner Frequencies</span>
              </div>
              <div className="flex gap-4 text-sm text-slate-400">
                <Link to="/frequencies" className="hover:text-white">Main Database</Link>
                <Link to="/" className="hover:text-white">Home</Link>
                <Link to="/dashboard" className="hover:text-white">Dashboard</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default FrequencyIndexPage;
